import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Hub from 'base/hub'
import Event from 'base/event'
import isUndefined from 'lodash/isUndefined'
import isEmpty from 'lodash/isEmpty'
import { connect } from 'react-redux'

import { requiresGDPRConsent } from 'base/lib/config'
import { getErrorMessage } from 'base/lib/errors'
import Link from 'base/react/components/link'
import { FormTypes } from 'base/constants/login'
import MagicLinkSignIn from 'base/components/forms/magic_link_sign_in'
import MagicLinkSignInWithPassword from 'base/components/forms/magic_link_sign_in_with_password'
import EmailForm from 'base/components/forms/email_form'
import FacebookSignInForm from 'mobile/components/forms/facebook_sign_in_form'
import SignUpForm from 'mobile/components/forms/sign_up_form'
import SignInForm from 'mobile/components/forms/sign_in_form'
import SocialSignInForm, { SocialTypes } from 'mobile/components/forms/social_sign_in_form'
import GoogleConnectButton from 'base/react/components/google_connect_button'
import { getErrorsForAuthData, getErrorsForUserAccountData } from 'base/validators'
import { get } from 'base/lib/network'
import apiUrl from 'base/api_url'
import { signIn, createAccount, saveVisitor } from 'base/actions/asynchronous'
import { receiveGuestCheckoutEmail } from 'base/actions/synchronous'
import routeTo from 'base/lib/route_to'
import TermsOfService from 'base/components/sign_up/terms_of_service'
import PasswordReset from 'base/containers/password_reset/password_reset'

class Login extends Component {
  static propTypes = {
    checkEmailAvailability: PropTypes.func.isRequired,
    ctaText: PropTypes.string,
    email: PropTypes.string,
    handleRefreshScroller: PropTypes.func,
    hasRepeatUserCookie: PropTypes.bool,
    initialForm: PropTypes.string,
    isNewUser: PropTypes.bool,
    message: PropTypes.string,
    onBackToLogin: PropTypes.func,
    onChangeFormType: PropTypes.func,
    onEmailChecked: PropTypes.func,
    onError: PropTypes.func,
    onSignInSuccess: PropTypes.func,
    onSignUpSuccess: PropTypes.func,
    signUp: PropTypes.func.isRequired,
    signIn: PropTypes.func.isRequired,
    siteLocation: PropTypes.string.isRequired,
    visitor: PropTypes.object,
    setGuestCheckoutEmail: PropTypes.func,
    saveVisitor: PropTypes.func,
  }

  constructor(props) {
    super(props)

    this.state = {
      isNewUser: this.getInitialNewUserState(),
      isPasswordlessUser: false,
      genericErrorMessage: '',
      errorsForCheckEmail: {},
      errorsForSignInForm: {},
      errorsForSignUpForm: {},
      email: props.email || '',
      userNeedstoUseSocialType: null, // one of SocialTypes
      formType: this.props.initialForm,
    }
  }

  componentDidMount() {
    Hub.pub(Event.Login.MODAL_VIEW, { site_location: this.props.siteLocation })
  }

  handleChangeFormType = formType => {
    this.setState({ formType })
    this.props.onChangeFormType?.(formType)
  }

  handleCheckEmail = async email => {
    const errors = getErrorsForUserAccountData({ email })
    const onSuccess = ({
      is_available,
      is_facebook_connected,
      is_google_connected,
      has_password,
    }) => {
      this.setState({
        email: email,
        isNewUser: is_available,
        genericErrorMessage: null,
        isPasswordlessUser: !is_available && !has_password,
      })

      if (this.props.onEmailChecked) {
        this.props.onEmailChecked({
          isNewUser: is_available,
          isPasswordlessUser: !is_available && !has_password,
        })
      }

      const isGuest = !is_facebook_connected && !is_google_connected && !has_password

      if (is_available) {
        if (isGuest) {
          this.props.setGuestCheckoutEmail(email)
          this.props.saveVisitor({ email })
          routeTo('/checkout/shipping')
          return
        }
        Hub.pub(Event.Login.SHOW_SIGN_UP_VIEW, {
          site_location: this.props.siteLocation,
        })
        this.handleChangeFormType(FormTypes.SIGN_UP)
      } else if (is_facebook_connected && !has_password) {
        this.setState({ userNeedstoUseSocialType: SocialTypes.FACEBOOK })
      } else if (is_google_connected && !has_password) {
        this.setState({ userNeedstoUseSocialType: SocialTypes.GOOGLE })
      } else if (!has_password) {
        this.handleChangeFormType(FormTypes.RESET)
      } else {
        Hub.pub(Event.Login.SHOW_SIGN_IN_VIEW, {
          site_location: this.props.siteLocation,
        })
        this.handleChangeFormType(FormTypes.SIGN_IN)
      }
    }

    Hub.pub(Event.Login.EMAIL_ENTERED, {
      site_location: this.props.siteLocation,
    })

    if (!isEmpty(errors)) {
      this.setState({ errorsForCheckEmail: errors })
      return
    }

    this.setState({ isLoading: true })

    this.props
      .checkEmailAvailability({ email, site_location: this.props.siteLocation })
      .always(() => this.setState({ isLoading: false }))
      .fail(error => {
        this.setState({
          errorsForCheckEmail: { email: getErrorMessage(error) },
        })
      })
      .then(onSuccess)
  }

  getInitialNewUserState = () => {
    if (this.props.hasRepeatUserCookie) {
      return false
    } else if (this.props.isNewUser !== undefined) {
      return this.props.isNewUser
    } else if (
      this.props.initialForm === FormTypes.EMAIL ||
      this.props.initialForm === FormTypes.FACEBOOK
    ) {
      return undefined
    } else if (this.props.initialForm) {
      return this.props.initialForm === FormTypes.SIGN_UP
    } else {
      return null
    }
  }

  handleSignUp = user => {
    const errors = getErrorsForAuthData(user)

    if (!isEmpty(errors)) {
      this.setState({ errorsForSignUpForm: errors })
      return
    }

    this.setState({ isLoading: true })
    this.props
      .signUp(user, this.props.siteLocation)
      .then(this.props.onSignUpSuccess)
      .fail(response => {
        this.setState(
          {
            isLoading: false,
            genericErrorMessage: getErrorMessage(response),
          },
          () => this.props.onError && this.props.onError(),
        )
      })
  }

  handleSignIn = async session => {
    Hub.pub(Event.Login.EMAIL_PW, { site_location: this.props.siteLocation })

    const errors = getErrorsForAuthData(session)
    if (!isEmpty(errors)) {
      this.setState({ errorsForSignInForm: errors })
      return
    }

    this.setState({ isLoading: true })

    this.props
      .signIn(session, this.props.siteLocation)
      .then(this.props.onSignInSuccess)
      .catch(response => {
        if (window.turnstile) {
          window.turnstile.reset()
        }
        this.setState(
          {
            isLoading: false,
            genericErrorMessage: getErrorMessage(response),
          },
          () => this.props.onError && this.props.onError(),
        )
      })
  }

  handleSocialSuccess = ({ isNewUser }) => {
    if (isNewUser) {
      this.props.onSignUpSuccess && this.props.onSignUpSuccess()
    } else {
      this.props.onSignInSuccess && this.props.onSignInSuccess()
    }
  }

  handleGenericError = response => {
    this.setState({
      genericErrorMessage: getErrorMessage(response),
    })
  }

  handleSwitchToEmail = () => {
    this.setState({
      genericErrorMessage: null,
    })
    this.handleChangeFormType(FormTypes.EMAIL)
  }

  handleSwitchToSignUp = () => {
    this.setState({
      isNewUser: true,
      genericErrorMessage: null,
    })
    this.handleChangeFormType(FormTypes.SIGN_UP)
    Hub.pub(Event.Login.SWITCH_TO_SIGN_UP, {
      site_location: this.props.siteLocation,
    })
  }

  handleSwitchToSignIn = () => {
    this.setState({
      isNewUser: false,
      genericErrorMessage: null,
      userNeedstoUseSocialType: null,
    })
    this.handleChangeFormType(FormTypes.SIGN_IN)
    Hub.pub(Event.Login.SWITCH_TO_SIGN_IN, {
      site_location: this.props.siteLocation,
    })
  }

  renderSocialButtons() {
    return (
      <div className="login__social">
        <p className="login__social-message">Or continue with</p>
        <div className="login__social-button-wrapper">
          <GoogleConnectButton
            showIcon
            onSuccess={this.handleSocialSuccess}
            onError={this.handleGenericError}
            siteLocation={this.props.siteLocation}
            className="login__social-button"
          />
        </div>
      </div>
    )
  }

  renderSignInForm() {
    let headerContent = "Don't have an account? "
    if (this.props.hasRepeatUserCookie) {
      headerContent = (
        <span>
          Log in to your Everlane account.
          <br />
          {headerContent}
        </span>
      )
    }

    return (
      <div>
        <SignInForm
          isLoading={this.state.isLoading}
          initialEmail={
            this.props.hasRepeatUserCookie ? this.props.visitor.email : this.state.email
          }
          errors={this.state.errorsForSignInForm}
          onSubmit={this.handleSignIn}
          ctaText={this.props.ctaText}
        />
        <div style={{ marginTop: '20px' }}>
          {!this.state.isLoading &&
            this.state.formType !== FormTypes.FACEBOOK &&
            this.renderSocialButtons()}
        </div>
        <div className="login__header">
          {headerContent}
          <Link className="login__header-link" onClick={this.handleSwitchToSignUp}>
            Sign up
          </Link>
        </div>
        <Link
          className="login__footer-link"
          onClick={() => this.handleChangeFormType(FormTypes.RESET)}
        >
          Reset password
        </Link>
      </div>
    )
  }

  renderSignUpForm() {
    return (
      <div>
        <SignUpForm
          isLoading={this.state.isLoading}
          initialEmail={this.state.email}
          errors={this.state.errorsForSignUpForm}
          onSubmit={this.handleSignUp}
          siteLocation={this.props.siteLocation}
          ctaText={this.props.ctaText}
          refreshParentScroller={this.props.handleRefreshScroller}
          defaultOptIn={!requiresGDPRConsent()}
        />
        <div
          style={{
            display: 'flex',
            margin: '20px 0 0',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Already have an account?{' '}
          <Link className="login__footer-sign-in-link" onClick={this.handleSwitchToSignIn}>
            Sign In
          </Link>
        </div>
      </div>
    )
  }

  renderPasswordReset() {
    return (
      <PasswordReset
        email={this.state.email}
        exitReset={this.props.onBackToLogin}
        isPasswordlessUser={this.state.isPasswordlessUser}
      />
    )
  }

  renderSocialSignInForm() {
    return (
      <SocialSignInForm
        socialType={this.state.userNeedstoUseSocialType}
        onClickSignIn={this.handleSwitchToSignUp}
        onClickResetPassword={() => routeTo('/reset', { hard: true })}
        siteLocation={this.props.siteLocation}
      />
    )
  }

  renderMagicLinkSignInWithPassword() {
    return (
      <MagicLinkSignInWithPassword
        initialEmail={this.state.email}
        errors={this.state.errorsForSignInForm}
        siteLocation={this.props.siteLocation}
        onClickSwitchToPasswordless={() =>
          this.handleChangeFormType(FormTypes.MAGIC_LINK_PASSWORDLESS)
        }
        onSubmit={this.handleSignIn}
        isLoading={this.state.isLoading}
      />
    )
  }

  renderMagicLinkSignIn() {
    return (
      <MagicLinkSignIn
        initialEmail={this.state.email}
        siteLocation={this.props.siteLocation}
        isPasswordlessUser={this.state.isPasswordlessUser}
        onServerError={this.handleGenericError}
        onClickSwitchToPassword={() => this.handleChangeFormType(FormTypes.MAGIC_LINK_PASSWORD)}
      />
    )
  }

  renderAppropriateForm() {
    if (isUndefined(this.state.isNewUser)) {
      if (this.state.formType === FormTypes.FACEBOOK) {
        return (
          <FacebookSignInForm
            onSuccess={this.handleSocialSuccess}
            onError={this.handleGenericError}
            onClickLoginWithEmail={this.handleSwitchToEmail}
            siteLocation={this.props.siteLocation}
          />
        )
      } else {
        return (
          <EmailForm
            buttonText={this.props.ctaText}
            type="default"
            errors={this.state.errorsForCheckEmail}
            isLoading={this.state.isLoading}
            onSubmit={this.handleCheckEmail}
          />
        )
      }
    } else if (this.state.formType === FormTypes.MAGIC_LINK_PASSWORD) {
      return this.renderMagicLinkSignInWithPassword()
    } else if (this.state.formType === FormTypes.MAGIC_LINK_PASSWORDLESS) {
      return this.renderMagicLinkSignIn()
    } else if (this.state.isNewUser) {
      return this.renderSignUpForm()
    } else if (this.state.userNeedstoUseSocialType) {
      return this.renderSocialSignInForm()
    } else if (this.state.formType === FormTypes.RESET) {
      return this.renderPasswordReset()
    } else {
      return this.renderSignInForm()
    }
  }

  render() {
    return (
      <div className="login">
        {this.state.genericErrorMessage && (
          <div className="login__error-message">{this.state.genericErrorMessage}</div>
        )}
        {this.props.message && <p className="login__message">{this.props.message}</p>}
        <div className="login__content-container">{this.renderAppropriateForm()}</div>
        {this.state.isNewUser === undefined &&
          !this.state.isLoading &&
          this.state.formType !== FormTypes.FACEBOOK &&
          this.renderSocialButtons()}

        {this.state.isNewUser === undefined && <TermsOfService />}
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    visitor: state.session.visitor,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    setGuestCheckoutEmail: email => dispatch(receiveGuestCheckoutEmail(email)),
    signIn: (sessionData, siteLocation) => dispatch(signIn(sessionData, { siteLocation })),
    signUp: (userData, siteLocation) => dispatch(createAccount(userData, { siteLocation })),
    checkEmailAvailability: authData => get(apiUrl('users/availability'), authData),
    saveVisitor: visitor => dispatch(saveVisitor(visitor)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
